/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import React, { useState } from "react";
import Navbar from "@/shared/Navbar";
import { useHeroSection } from "@/hooks/useHeroSection";
import { motion } from "framer-motion";
import {
  PhoneCall,
  MessageCircle,
  FileText,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
const API = process.env.NEXT_PUBLIC_API_URL;
const ContactPage = () => {
  // 🟢 Hero Section
  const HeroSection = useHeroSection({
    title: "Let’s Talk About Your Next Move",
    subtitle:
      "Our local experts are ready to help you navigate every step — from listing to closing.",
    backgroundImage: "/contact-hero.jpg",
    buttonText: "Speak With an Expert",
    buttonLink: "/storefront",
  });

  // 🧩 Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    agree: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === "checkbox";
    setFormData({
      ...formData,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/v1/contact`, formData);
      if (res.data.success) {
        toast.success("Message sent successfully!");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
          agree: false,
        });
      } else {
        toast.error("Failed to send. Try again later.");
      }
    } catch (err) {
      toast.error("Server error. Please try again later.");
    }
  };

  return (
    <div className="bg-[#f8fafc] min-h-screen font-inter">
      {/* 🧭 Navbar */}
      <Navbar
        defaultLogo="/logo/asklocal-white-logo.png"
        scrolledLogo="/logo/color-full-logo.png"
        isLight
        labelColor="#ffffff"
        labelHoverColor="#EB9C2C"
        buttonBg="#EB9C2C"
        buttonTextColor="#FFFFFF"
        buttonHoverBg="#4177BC"
        buttonHoverText="#ffffff"
        ctaText="Looking to Buy/Sell?"
        ctaLink="/buy-sell"
        navItems={[
          { label: "Home", href: "/" },
          { label: "About Us", href: "/about-us" },
          { label: "Pro-finder", href: "/pro-finder" },
          { label: "FAQ", href: "/faq" },
        ]}
      />

      {/* 🖼 Hero */}
      <div>{HeroSection}</div>

      {/* ✨ Introduction */}
      <section className="container mx-auto px-6 py-20 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-6xl judson-regular text-gray-900 mb-6"
        >
          Get Your Free Consultation
        </motion.h2>
        <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
          Say goodbye to confusing real estate jargon and stressful
          transactions. Whether you’re buying, selling, or investing — AskLocal
          connects you with trusted professionals who make the process seamless.
        </p>
      </section>

      {/* 🧭 Contact Options */}
      <section className="container mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
        {[
          {
            icon: <PhoneCall className="w-10 h-10 text-[#EB9C2C]" />,
            title: "Call Our Concierge",
            text: "1440-999-2439",
          },
          {
            icon: <MessageCircle className="w-10 h-10 text-[#4177BC]" />,
            title: "Live Chat Support",
            text: "Connect with one of our local specialists instantly.",
          },
          {
            icon: <FileText className="w-10 h-10 text-[#EB9C2C]" />,
            title: "Submit a Contact Form",
            text: "Send your message — we typically respond within 24 hours.",
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            whileHover={{ y: -8, boxShadow: "0 10px 25px rgba(0,0,0,0.08)" }}
            transition={{ type: "spring", stiffness: 150, damping: 20 }}
            className="p-8 rounded-3xl bg-white shadow-[0_4px_25px_rgba(0,0,0,0.05)] border border-gray-100 hover:border-[#4177BC]/40 text-center"
          >
            <div className="flex justify-center mb-4">{item.icon}</div>
            <h3 className="inter-semibold text-xl text-gray-800 mb-2">
              {item.title}
            </h3>
            <p className="text-gray-600 text-base">{item.text}</p>
          </motion.div>
        ))}
      </section>

      {/* 🧭 Info + Form Split */}
      <section className="relative max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-16 items-start">
        {/* 🔹 Left Info Panel */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          <h3 className="text-5xl judson-regular text-black">
            Let’s Get You Connected
          </h3>
          <p className="text-gray-600">
            From listing your home to finding your dream property — we’ll match
            you with the right local professional for your needs.
          </p>

          <div className="mt-8 space-y-5">
            <div className="flex items-center gap-3">
              <PhoneCall className="w-10 h-10 text-[#EB9C2C]" />
              <span className="text-gray-800 inter-medium">1-833-512-4663</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-10 h-10 text-[#4177BC]" />
              <span className="text-gray-700 inter-medium">
                Concierge@asklocal.com
              </span>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-10 h-10 text-[#EB9C2C]" />
              <span className="text-gray-700 inter-medium">
                17415 Northwood Avenue, Ste 201
                <br />
                Lakewood, Ohio 44107
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-10 h-10 text-[#4177BC]" />
              <span className="text-gray-700 inter-medium">
                Mon–Sat: 10AM–5PM | Sun Closed
              </span>
            </div>
          </div>
        </motion.div>

        {/* 🔹 Right Form Panel */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.06)] p-10 md:p-12 border border-gray-100 backdrop-blur-md"
        >
          <h4 className="text-2xl font-semibold text-gray-900 mb-8">
            Fill Out the Form
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <input
              name="firstName"
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="border border-gray-300 rounded-xl py-3 px-4 bg-gray-50/40 text-gray-700 focus:ring-2 focus:ring-[#4177BC] focus:border-transparent transition-all outline-none"
            />
            <input
              name="lastName"
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="border border-gray-300 rounded-xl py-3 px-4 bg-gray-50/40 text-gray-700 focus:ring-2 focus:ring-[#4177BC] focus:border-transparent transition-all outline-none"
            />
            <input
              name="email"
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              className="border border-gray-300 rounded-xl py-3 px-4 bg-gray-50/40 text-gray-700 focus:ring-2 focus:ring-[#4177BC] focus:border-transparent transition-all outline-none"
            />
            <input
              name="phone"
              type="text"
              placeholder="Phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="border border-gray-300 rounded-xl py-3 px-4 bg-gray-50/40 text-gray-700 focus:ring-2 focus:ring-[#4177BC] focus:border-transparent transition-all outline-none"
            />
          </div>

          <textarea
            name="message"
            placeholder="Message"
            rows={5}
            value={formData.message}
            onChange={handleChange}
            className="mt-6 w-full border border-gray-300 rounded-xl py-3 px-4 bg-gray-50/40 text-gray-700 focus:ring-2 focus:ring-[#EB9C2C] focus:border-transparent transition-all resize-none"
            required
          ></textarea>

          <div className="mt-6 flex items-start gap-3">
            <input
              type="checkbox"
              name="agree"
              checked={formData.agree}
              onChange={handleChange}
              className="mt-1 accent-[#EB9C2C]"
              required
            />
            <label className="text-sm text-gray-600 leading-snug">
              I agree to receive updates from AskLocal. Message and data rates
              may apply.
            </label>
          </div>

          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            type="submit"
            className="mt-8 w-full py-3.5 bg-[#EB9C2C] text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Submit
          </motion.button>
        </motion.form>
      </section>

      {/* 🌟 After Form Section */}
      <section className="relative overflow-hidden py-20 mt-10">
        <div className="absolute inset-0 bg-linear-to-br from-[#EB9C2C]/10 via-white to-[#4177BC]/10 blur-3xl -z-10" />
        <div className="max-w-6xl mx-auto text-center px-6">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            🌟 Why Homeowners{" "}
            <span className="text-[#4177BC]">Love AskLocal</span>
          </motion.h2>

          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
            Trusted by thousands of homeowners and agents across the U.S. —
            AskLocal brings real expertise, fast response, and a platform
            designed to make real estate stress-free.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Verified Local Experts",
                desc: "Every agent on AskLocal is vetted to ensure top-tier local knowledge and trustworthiness.",
                icon: "🏡",
              },
              {
                title: "Seamless Process",
                desc: "From listing to closing, everything happens in one place — fast, secure, and transparent.",
                icon: "⚡",
              },
              {
                title: "Real People, Real Help",
                desc: "No bots. Just genuine experts ready to help you make smarter real estate decisions.",
                icon: "🤝",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 140 }}
                className="bg-white/70 backdrop-blur-xl border border-gray-100 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 text-left"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 bg-[#4177BC] rounded-2xl text-white py-10 px-8 shadow-lg">
            <h3 className="text-2xl font-semibold mb-2">
              ✨ Limited-Time Offer
            </h3>
            <p className="text-lg mb-4">
              Book your free consultation today and get matched with 3 verified
              local agents instantly!
            </p>
            <motion.a
              href="/storefront"
              whileHover={{ scale: 1.05 }}
              className="inline-block mt-3 bg-white text-[#4177BC] font-semibold px-8 py-3 rounded-full hover:bg-gray-100 transition-all"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </section>

      <div className="w-full h-1.5 bg-[#]" />
    </div>
  );
};

export default ContactPage;
